import {Menu} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {ServiceTypeEntity} from "@ots-frontend/ots-data";
import {useCallback, useMemo} from "react";
import {useAuth} from "../../../providers/AuthProvider/AuthProvider";
import {useServiceTypeAction} from "./useServiceTypeAction";
import {IconEdit, IconTrash} from "@tabler/icons-react";

const ALL_ACTIONS = [
  "EDIT",
  "DELETE",
] as const;

export type ServiceTypeAction = typeof ALL_ACTIONS[number];

export type ServiceTypeActionMenuDropdownProps = {
  serviceType: ServiceTypeEntity
  actions?: ServiceTypeAction[]
  onActionClick?: (action: ServiceTypeAction) => void
  showEmpty?: boolean
}

export const ServiceTypeActionMenuDropdown = (props: ServiceTypeActionMenuDropdownProps) => {
  const {
    serviceType,
    actions = ALL_ACTIONS,
    onActionClick,
    showEmpty = true
  } = props

  const {hasAccess} = useAuth()
  const {t} = useTranslation(["common"])

  const visibleActions: Set<ServiceTypeAction> = useMemo(() => {
    const enabledActions = new Set<ServiceTypeAction>()
    const actionsSet = actions ? new Set<ServiceTypeAction>(actions) : new Set<ServiceTypeAction>()

    if (hasAccess("servicetype_edit")) {
      if (actionsSet.has("EDIT")) {
        enabledActions.add("EDIT")
      }
    }

    if (hasAccess("servicetype_delete")) {
      if (actionsSet.has("DELETE")) {
        enabledActions.add("DELETE")
      }
    }

    return enabledActions
  }, [serviceType, actions, hasAccess])

  const {
    onServiceTypeActionClick
  } = useServiceTypeAction()

  const _onActionClick = useCallback((action: ServiceTypeAction) => {
    if (onActionClick) {
      onActionClick(action)
    } else {
      onServiceTypeActionClick(serviceType, action)
    }
  }, [onActionClick, onServiceTypeActionClick, serviceType])

  return (
    <>
      {(showEmpty && visibleActions.size === 0) &&
        <Menu.Item>
          {t("common.action.none", "No Actions")}
        </Menu.Item>
      }
      {visibleActions.has("EDIT") &&
        <Menu.Item icon={<IconEdit size={14}/>} onClick={() => _onActionClick("EDIT")}>
          {t("common.action.edit", "Edit")}
        </Menu.Item>
      }
      {visibleActions.has("DELETE") &&
        <>
          {visibleActions.size > 1 && <Menu.Divider/>}
          <Menu.Item
            onClick={() => _onActionClick("DELETE")}
            icon={<IconTrash size={14}/>}
            color="red">
            {t("common.action.delete", "Delete")}
          </Menu.Item>
        </>
      }
    </>
  )
}
